import { atom } from 'jotai'
import type { PrimitiveAtom } from 'jotai'
import type { Map } from '@/js/types'
import { mapSessionAtom, type MapId } from './mapAtoms.ts'
import { normalizeCenter } from '@/js/helper/normalizeCenter.ts'

export type MapRequest = (mapId: MapId) => Promise<Response>

type MapLoadState = {
    isLoading: boolean
    error: Error | null
}

const initialMapLoadState: MapLoadState = {
    isLoading: false,
    error: null,
}

const mapSessionRequestIdAtom: PrimitiveAtom<number> = atom(0)

export const mapLoadStateAtom: PrimitiveAtom<MapLoadState> = atom<MapLoadState>(initialMapLoadState)

export const isMapLoadingAtom = atom((get) => get(mapLoadStateAtom).isLoading)
export const mapLoadErrorAtom = atom((get) => get(mapLoadStateAtom).error)

export const resetMapSessionAtom = atom(null, (_get, set) => {
    set(mapSessionRequestIdAtom, (requestId) => requestId + 1)
    set(mapSessionAtom, {
        id: null,
        map: null,
    })
    set(mapLoadStateAtom, initialMapLoadState)
})

export const startMapSessionAtom = atom(null, (_get, set, mapId: MapId) => {
    set(mapSessionRequestIdAtom, (requestId) => requestId + 1)
    set(mapSessionAtom, {
        id: mapId,
        map: null,
    })
    set(mapLoadStateAtom, {
        isLoading: true,
        error: null,
    })
})

export const loadMapAtom = atom(
    null,
    async (get, set, { mapId, request }: { mapId: MapId; request: MapRequest }): Promise<Map | null> => {
        set(startMapSessionAtom, mapId)

        const requestId = get(mapSessionRequestIdAtom)

        try {
            const response = await request(mapId)

            if (get(mapSessionRequestIdAtom) !== requestId) {
                return null
            }

            if (!response.ok) {
                set(mapLoadStateAtom, {
                    isLoading: false,
                    error: new Error(`Unable to load map (${response.status})`),
                })

                return null
            }

            const mapResponse = (await response.json()) as Map
            const center: [number, number] =
                normalizeCenter(mapResponse.center) ?? (mapResponse.map_type === 'geographic' ? [0, 0] : null)

            if (!center && mapResponse.map_type == 'geographic') {
                set(mapLoadStateAtom, {
                    isLoading: false,
                    error: new Error('The map has an invalid center coordinate'),
                })

                return null
            }

            const map: Map = {
                ...mapResponse,
                center,
            }

            if (get(mapSessionRequestIdAtom) !== requestId) {
                return null
            }

            set(mapSessionAtom, {
                id: mapId,
                map,
            })
            set(mapLoadStateAtom, initialMapLoadState)

            return map
        } catch (error) {
            if (get(mapSessionRequestIdAtom) !== requestId) {
                return null
            }

            set(mapLoadStateAtom, {
                isLoading: false,
                error: error instanceof Error ? error : new Error('Unable to load map'),
            })

            return null
        }
    }
)
